import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
export function toTypeaheadMenuSections(rows) {
    const sections = [];
    for (const [index, row] of rows.entries()) {
        const previousSection = sections.at(-1);
        if (previousSection !== undefined && previousSection.sectionId === row.sectionId) {
            previousSection.rows.push({ row, index });
            continue;
        }
        sections.push({
            sectionId: row.sectionId,
            sectionTitle: row.sectionTitle,
            rows: [{ row, index }],
        });
    }
    return sections;
}
/**
 * Groups neighboring rows when their provider, section, group ID, and group index match.
 * It keeps each row's original index for keyboard navigation. Every group has at least one row.
 *
 * @example
 * ```ts
 * const search: ResolvedRow = {
 *   item: { id: 'search', label: 'Search' },
 *   providerIndex: 0,
 *   sectionId: null,
 *   sectionTitle: null,
 *   groupId: 'modes',
 *   groupIndex: 0,
 * };
 * const research: ResolvedRow = {
 *   item: { id: 'research', label: 'Research' },
 *   providerIndex: 0,
 *   sectionId: null,
 *   sectionTitle: null,
 *   groupId: 'modes',
 *   groupIndex: 0,
 * };
 * const computer: ResolvedRow = {
 *   item: { id: 'computer', label: 'Computer' },
 *   providerIndex: 0,
 *   sectionId: null,
 *   sectionTitle: null,
 *   groupId: 'modes',
 *   groupIndex: 1,
 * };
 * toTypeaheadMenuEntries([
 *   { row: search, index: 0 },
 *   { row: research, index: 1 },
 *   { row: computer, index: 2 },
 * ]);
 * // => [
 * //   {
 * //     type: 'group',
 * //     rows: [{ row: search, index: 0 }, { row: research, index: 1 }],
 * //     showSeparator: false,
 * //   },
 * //   {
 * //     type: 'group',
 * //     rows: [{ row: computer, index: 2 }],
 * //     showSeparator: true,
 * //   },
 * // ]
 * ```
 */
export function toTypeaheadMenuEntries(rows) {
    const entries = [];
    let groupCount = 0;
    for (const item of rows) {
        const previousEntry = entries.at(-1);
        if (previousEntry?.type === 'group' &&
            belongsToSameGroup(previousEntry.rows[0].row, item.row)) {
            previousEntry.rows.push(item);
            continue;
        }
        if (item.row.groupId === undefined) {
            entries.push({ type: 'item', item });
            continue;
        }
        entries.push({ type: 'group', rows: [item], showSeparator: groupCount > 0 });
        groupCount += 1;
    }
    return entries;
}
export function getRowDomId(idBase, row) {
    return `${idBase}-${row.providerIndex}-${row.item.id}`;
}
export function getMenuEntryKey(entry, idBase) {
    switch (entry.type) {
        case 'item':
            return getRowDomId(idBase, entry.item.row);
        case 'group': {
            const { row } = entry.rows[0];
            return `${row.providerIndex}-${row.groupIndex}-${row.groupId}-${row.item.id}`;
        }
        default:
            return assertUnreachableValue(entry);
    }
}
function belongsToSameGroup(left, right) {
    return (left.providerIndex === right.providerIndex &&
        left.sectionId === right.sectionId &&
        left.groupId === right.groupId &&
        left.groupIndex === right.groupIndex);
}
//# sourceMappingURL=typeaheadMenuData.js.map